// The whole web page of a project's app (ADR 0021): its own sign-in page, its app bar and account menu,
// the project's apps (the app view), the full page of every pinned companion, and on every project page
// the companions' overlays (the tours) and the chart windows. A project's web/main.tsx renders
// <AppShell companions={…}/> with the `./app` entries of the SDKs it pins.
//
// NO companion UI by default (owner, 2026-11-04: "companions UI should be disabled for everyone by default for all
// apps"): the shell draws no speed dial, pill, launcher, chat window or companion window over the app, for anyone,
// admins included. The companions keep working (their routes, their pages at /<path>, the headless chat of
// @cortex/app-sdk/chat); an app that needs one draws its entry in its own UI, in its own design.
import { ACCOUNT_SECURITY_PAGE } from '@cortex/app-host/contract';
import { ConfirmProvider, Toaster } from '@cortex/ui';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import * as React from 'react';
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouterProvider,
  useLocation,
  useMatches,
  type RouteObject,
} from 'react-router';
import { applyBranding } from './branding.ts';
import { AppView } from './AppView.tsx';
import { AppAuthProvider, RequireSignIn, SignedOutOnly } from './auth.tsx';
import { ChartWindows } from './ChartWindows.tsx';
import { CompanionPagesProvider, type AppCompanion } from './companions.tsx';
import { shownPath } from './paths.ts';
import { appInfo, moduleRuns } from './session.ts';
import { AppSignIn, signInSettled } from './SignIn.tsx';

/** The page's query cache: the SDK views share it (a chat tool that changed data marks it stale). */
export const appQueryClient = new QueryClient({
  defaultOptions: { queries: { retry: false, refetchOnWindowFocus: false, staleTime: 0 } },
});

/** The home app's first page (/, its query kept). */
const Home = () => <Navigate to={{ pathname: '/', search: useLocation().search }} replace />;

function RouteError() {
  return (
    <div className="p-6 text-sm text-page-muted" data-testid="route-error">
      This page could not be shown.
    </div>
  );
}

function Layout({ companions }: { companions: AppCompanion[] }) {
  const info = appInfo();
  const slug = info.project.slug;
  const home = info.home ?? info.apps[0]?.key ?? null;
  // The app open on the page: the home app at its own addresses (its route is the path), another at /apps/<app>.
  const pathname = useLocation().pathname;
  const homeOpen = useMatches().some((m) => (m.handle as { home?: boolean } | undefined)?.home === true);
  const opened = /^\/apps\/([^/]+)\/?$/.exec(pathname)?.[1];
  const app = homeOpen ? home : opened ? decodeURIComponent(opened) : null;
  React.useEffect(signInSettled, []);
  return (
    <>
      <div className="h-screen flex w-full overflow-hidden bg-page-bg" data-testid="standalone-shell">
        {/* No bar: the app (its frame) or an SDK page, full-bleed, as in cortex's preview. */}
        <main className="flex-1 flex flex-col overflow-hidden min-w-0">
          <div className="flex-1 overflow-auto" data-testid="page" data-tour="page">
            {/* The home app is this layout's own: '/' and every route of it share one frame, never remounted. */}
            {homeOpen ? <AppView /> : <Outlet />}
          </div>
        </main>
      </div>
      {companions.map((c) => (c.Overlay ? <c.Overlay key={c.module} slug={slug} app={app} /> : null))}
      <ChartWindows />
    </>
  );
}

/** The app's own AI page (its admins'), a page of the shell beside the companions'. */
const AI_PAGE = 'ai-use';

export function appRoutes(companions: AppCompanion[]): RouteObject[] {
  const pinned = companions.filter((c) => moduleRuns(c.module));
  const pages = Object.assign(
    { [AI_PAGE]: () => import('./AiUsePage.tsx').then((m) => m.AiUsePage) },
    ...pinned.map((c) => c.pages ?? {}),
  ) as Record<string, () => Promise<React.ComponentType>>;
  return [
    {
      element: <SignedOutOnly />,
      children: [{ path: '/login', element: <AppSignIn /> }],
    },
    {
      path: '/',
      element: <RequireSignIn />,
      errorElement: <RouteError />,
      children: [
        {
          path: ACCOUNT_SECURITY_PAGE.slice(1),
          lazy: async () => ({ Component: (await import('./SecurityPage.tsx')).SecurityPage }),
          hydrateFallbackElement: <div className="min-h-screen bg-page-bg" />,
          errorElement: <RouteError />,
        },
        {
          element: (
            <CompanionPagesProvider pages={pages}>
              <Layout companions={pinned} />
            </CompanionPagesProvider>
          ),
          children: [
            // The home app's first page and every address the shell does not name (its routes): the layout holds
            // its frame, so it stays as the route moves.
            { index: true, handle: { home: true }, errorElement: <RouteError /> },
            { path: 'apps', element: <Home /> },
            { path: 'apps/:viewName', element: <AppView />, errorElement: <RouteError /> },
            ...Object.entries(pages).map(([path, load]): RouteObject => ({
              path,
              lazy: async () => ({ Component: await load() }),
              hydrateFallbackElement: <div className="min-h-full bg-page-bg" />,
              errorElement: <RouteError />,
            })),
            { path: '*', handle: { home: true }, errorElement: <RouteError /> },
          ],
        },
      ],
    },
  ];
}

/** The page's router on the browser's address: the home app's /apps/<home> and /apps are shown at / first. */
export function createAppRouter(routes: RouteObject[]) {
  const i = appInfo();
  const at = window.location;
  const want = shownPath(at.pathname, { home: i.home ?? i.apps[0]?.key ?? null });
  if (want !== at.pathname)
    window.history.replaceState(window.history.state, '', `${want}${at.search}${at.hash}`);
  return createBrowserRouter(routes);
}

export function AppShell({ companions }: { companions: AppCompanion[] }) {
  const router = React.useMemo(() => createAppRouter(appRoutes(companions)), [companions]);
  return (
    <QueryClientProvider client={appQueryClient}>
      <AppAuthProvider>
        <ConfirmProvider>
          <RouterProvider router={router} />
          <Toaster />
        </ConfirmProvider>
      </AppAuthProvider>
    </QueryClientProvider>
  );
}

/** Renders the app's page into #root, in the app's own colour from the first paint. */
export async function mountApp(companions: AppCompanion[]) {
  applyBranding();
  const { createRoot } = await import('react-dom/client');
  createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <AppShell companions={companions} />
    </React.StrictMode>,
  );
}
